/**
 * ---------------------------------------------------------------------------
 * VENNER — Tarjeta de contenido de la comunidad (UGC)
 * ---------------------------------------------------------------------------
 *
 * Cada tarjeta con video se reproduce sola cuando entra en pantalla y se pausa
 * al salir, para no gastar batería ni datos con videos que nadie está viendo.
 * Los botones de silencio y pausa son propios.
 *
 * Al quitar el silencio a una tarjeta, las demás se silencian: sólo puede
 * sonar una a la vez.
 */

{
  /**
   * En modo "uno a la vez" el carril funciona como una lista de reproducción: arranca por la primera
   * tarjeta, y cuando su video termina cede el turno a la siguiente. Tras la última vuelve a la primera.
   *
   * Las tarjetas no pueden decidir esto por su cuenta, así que el estado de la secuencia vive aquí,
   * asociado al carril que las contiene.
   */
  const secuencias = new WeakMap();
  const pendientes = new Set();

  function estadoDe(grupo) {
    if (!secuencias.has(grupo)) {
      secuencias.set(grupo, { indice: 0, corriendo: false, vigilante: 0 });
    }

    return secuencias.get(grupo);
  }

  function tarjetasDe(grupo) {
    return Array.from(grupo.querySelectorAll('venner-ugc-card'));
  }

  function detenerTodas(grupo, excepto) {
    for (const tarjeta of tarjetasDe(grupo)) {
      if (tarjeta !== excepto) tarjeta.detenerPorCoordinacion();
    }
  }

  /** Pone en marcha la tarjeta que toca y programa el relevo. */
  function reproducirTurno(grupo) {
    const estado = estadoDe(grupo);
    const tarjetas = tarjetasDe(grupo);

    if (tarjetas.length === 0) return;

    estado.indice = ((estado.indice % tarjetas.length) + tarjetas.length) % tarjetas.length;

    const activa = tarjetas[estado.indice];

    detenerTodas(grupo, activa);
    activa.reproducirSiProcede();
    activa.traerAlCarril();

    /*
      El relevo lo dispara el evento `ended`, pero si el navegador bloquea la reproducción ese evento no
      llega nunca y la secuencia se quedaría clavada. Este vigilante da por terminado el turno pasada la
      duración del video con un margen.
     */
    clearTimeout(estado.vigilante);

    const video = activa.querySelector('video');
    const duracion = video && Number.isFinite(video.duration) && video.duration > 0
      ? video.duration / activa.playbackRate
      : 12;

    estado.vigilante = setTimeout(() => avanzar(grupo, activa), (duracion + 2) * 1000);
  }

  function avanzar(grupo, desde) {
    const estado = estadoDe(grupo);
    const tarjetas = tarjetasDe(grupo);

    // Un `ended` rezagado de otra tarjeta no debe adelantar el turno.
    if (desde && tarjetas[estado.indice] !== desde) return;
    if (!estado.corriendo) return;

    estado.indice += 1;
    reproducirTurno(grupo);
  }

  /** Recalcula si el carril está a la vista y arranca o detiene la secuencia. */
  function coordinar(grupo) {
    if (pendientes.has(grupo)) return;

    pendientes.add(grupo);
    requestAnimationFrame(() => {
      pendientes.delete(grupo);

      const estado = estadoDe(grupo);
      const aLaVista = tarjetasDe(grupo).some((tarjeta) => tarjeta.visibilidad >= 0.25);

      if (aLaVista && !estado.corriendo) {
        estado.corriendo = true;
        reproducirTurno(grupo);
      } else if (!aLaVista && estado.corriendo) {
        estado.corriendo = false;
        clearTimeout(estado.vigilante);
        detenerTodas(grupo, null);
      }
    });
  }

  class VennerUgcCard extends HTMLElement {
    #abortController;
    #observer;
    #preloadObserver;
    #userPaused = false;

    visibilidad = 0;

    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.video.muted = true;
      this.toggleAttribute('muted', true);

      this.querySelectorAll('[data-vugc-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggle(), { signal });
      });

      this.querySelectorAll('[data-vugc-mute]').forEach((button) => {
        button.addEventListener('click', () => this.setMuted(!this.video.muted), { signal });
      });

      this.video.addEventListener('play', () => this.toggleAttribute('playing', true), { signal });
      this.video.addEventListener('pause', () => this.toggleAttribute('playing', false), { signal });

      if (this.playbackMode === 'one') {
        /*
          En modo lista el bucle tiene que estar apagado: con loop activo el video nunca emite `ended`
          y el turno jamás pasaría a la siguiente tarjeta.
        */
        this.video.loop = false;
        this.video.addEventListener('ended', () => avanzar(this.grupo, this), { signal });
      }

      if (this.autoplayInView) this.#observe();

      this.#precargarAlAcercarse();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#observer?.disconnect();
      this.#preloadObserver?.disconnect();
    }

    get autoplayInView() {
      return this.hasAttribute('autoplay-in-view');
    }

    get playbackRate() {
      const valor = parseFloat(this.getAttribute('playback-rate'));

      return Number.isFinite(valor) ? Math.min(2, Math.max(0.5, valor)) : 1;
    }

    get playbackMode() {
      return this.getAttribute('playback-mode') === 'one' ? 'one' : 'all';
    }

    get grupo() {
      return this.closest('.venner-ugc__track') || this.parentElement || document.body;
    }

    /** Lo llama el coordinador cuando esta tarjeta es la elegida del carril. */
    reproducirSiProcede() {
      if (!this.#userPaused) this.play();
    }

    /** Lo llama el coordinador cuando le toca ceder el turno a otra tarjeta. */
    detenerPorCoordinacion() {
      this.pause();
      if (this.video.currentTime > 0) this.video.currentTime = 0;
      if (!this.video.muted) this.setMuted(true);
    }

    /**
     * Desliza el carril para dejar a la vista la tarjeta que le toca reproducir. Sin esto la secuencia
     * seguiría corriendo fuera de pantalla y el visitante vería solo portadas quietas.
     *
     * Se mueve scrollLeft del carril, no scrollIntoView, que arrastraría también la página.
     */
    traerAlCarril() {
      const carril = this.closest('.venner-ugc__track');

      if (!carril || carril.scrollWidth <= carril.clientWidth) return;

      const destino = this.offsetLeft - (carril.clientWidth - this.offsetWidth) / 2;
      const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      carril.scrollTo({ left: Math.max(0, destino), behavior: suave ? 'smooth' : 'auto' });
    }

    get preloadMargin() {
      const valor = parseInt(this.getAttribute('preload-margin'), 10);

      return Number.isFinite(valor) ? Math.min(1500, Math.max(0, valor)) : 600;
    }

    /**
     * El video se dibuja con preload="none" para no descargar diez videos al abrir la página. Este
     * observador se adelanta a la tarjeta y lo pide antes de que entre en pantalla, de modo que al
     * llegar arranque al instante en vez de quedarse unos segundos en la portada.
     */
    #precargarAlAcercarse() {
      const margen = this.preloadMargin;

      if (margen === 0) return;

      this.#preloadObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.#preloadObserver.disconnect();
          this.video.preload = 'auto';

          /*
            load() reinicia el elemento y aborta lo que estuviera reproduciendo. Si la tarjeta ya arrancó
            —puede pasar cuando la precarga y la entrada en pantalla caen casi a la vez— se deja en paz:
            el vídeo ya se está descargando solo.
          */
          if (this.video.paused && this.video.readyState === 0 && this.video.currentTime === 0) {
            this.video.load();
          }
        },
        { rootMargin: `${margen}px` }
      );

      this.#preloadObserver.observe(this);
    }

    play() {
      if (!this.video) return;

      const rate = this.playbackRate;

      this.video.playbackRate = rate;

      // Algunos navegadores reinician la velocidad al cargar los metadatos.
      if (this.video.readyState === 0) {
        this.video.addEventListener('loadedmetadata', () => { this.video.playbackRate = rate; }, { once: true });
      }

      this.video.play().catch(() => {
        /* el navegador bloqueó la reproducción: se queda en la portada */
      });
    }

    pause() {
      this.video?.pause();
    }

    toggle() {
      if (this.video.paused) {
        this.#userPaused = false;
        this.play();
      } else {
        this.#userPaused = true;
        this.pause();
      }
    }

    setMuted(muted) {
      this.video.muted = muted;
      this.toggleAttribute('muted', muted);

      // Sólo una tarjeta con sonido a la vez.
      if (!muted) {
        document.querySelectorAll('venner-ugc-card:not([muted])').forEach((card) => {
          if (card !== this) card.setMuted(true);
        });
        this.play();
      }
    }

    #observe() {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.visibilidad = entry.intersectionRatio;

            if (this.playbackMode === 'one') {
              coordinar(this.grupo);
              continue;
            }

            if (entry.intersectionRatio >= 0.5) {
              if (!this.#userPaused) this.play();
            } else {
              this.pause();
              // Al salir de pantalla también se silencia, para que el sonido
              // no siga sonando desde una tarjeta que ya no se ve.
              if (!this.video.muted) this.setMuted(true);
            }
          }
        },
        { threshold: [0, 0.1, 0.25, 0.4, 0.5, 0.6, 0.75, 0.9, 1] }
      );

      this.#observer.observe(this);
    }
  }

  if (!window.customElements.get('venner-ugc-card')) {
    window.customElements.define('venner-ugc-card', VennerUgcCard);
  }
}
